import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client';
import { useCancelVerification, useConfirmVerification, useStartVerification } from '../api/queries';
import type { User } from '../api/types';
import { useToast } from './Toast';

function formatDate(value: string): string {
  return new Date(value).toLocaleString('de-DE');
}

/**
 * Verifizierungs-Wizard für die Spielfigur -- ersetzt das frühere freie
 * Eintragen eines Spielernamens. Solange keine Figur verifiziert ist, bleibt
 * praktisch das ganze Portal gesperrt (siehe `isPlayerVerified`,
 * `client/src/api/types.ts`, sowie `requireVerifiedPlayer` serverseitig).
 *
 * Drei Zustände, direkt aus `user` abgeleitet statt eigenem State:
 * - `user.verification` gesetzt   → Schritt 2 (Code eintragen, bestätigen).
 * - sonst + verifiziert           → aktuelle Figur zeigen, "Neu verifizieren" bietet Schritt 1 an.
 * - sonst + nicht verifiziert     → Schritt 1 direkt (Name eingeben).
 */
export function PlayerVerificationSection({ user }: { user: User }) {
  const start = useStartVerification();
  const confirm = useConfirmVerification();
  const cancel = useCancelVerification();
  const toast = useToast();

  const [playerName, setPlayerName] = useState(user.gamePlayerName ?? '');
  const [restarting, setRestarting] = useState(false);

  const busy = start.isPending || confirm.isPending || cancel.isPending;
  const startError = start.error instanceof ApiError ? start.error : null;
  const confirmError = confirm.error instanceof ApiError ? confirm.error : null;

  const submitStart = (event: FormEvent) => {
    event.preventDefault();
    start.mutate(
      { playerName: playerName.trim() },
      {
        onSuccess: () => setRestarting(false),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const submitConfirm = () => {
    confirm.mutate(undefined, {
      onSuccess: (data) => {
        toast.push('success', `Spielfigur "${data.user.gamePlayerName}" wurde verifiziert.`);
      },
      onError: (error) => toast.push('error', error.message),
    });
  };

  const abort = () => {
    if (!window.confirm('Verifizierung abbrechen?')) return;
    cancel.mutate(undefined, {
      onSuccess: () => toast.push('info', 'Verifizierung abgebrochen.'),
      onError: (error) => toast.push('error', error.message),
    });
  };

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.push('success', 'Code in die Zwischenablage kopiert.');
    } catch {
      toast.push('info', 'Kopieren nicht möglich — bitte den Code von Hand markieren.');
    }
  };

  // Schritt 2: ein Versuch läuft bereits.
  if (user.verification) {
    const { token, playerName: pendingName } = user.verification;
    return (
      <section className="card">
        <h2>Spielfigur verifizieren</h2>
        <p className="muted">
          Fast geschafft — trage den Code unten in dein Profil bei „{pendingName}" ein.
        </p>

        {confirmError && <div className="flash flash-error">{confirmError.message}</div>}

        <ol className="wizard-steps">
          <li>
            Im Spiel zu deinem Profil „{pendingName}" gehen und „Profil bearbeite" öffnen.
          </li>
          <li>
            Diesen Code in das Feld „Persönlicher Text" einfügen und speichern (der restliche Text
            darf stehen bleiben):
            <div className="token-value">
              <code>{token}</code>
              <button type="button" className="btn btn-sm" onClick={() => copyToken(token)}>
                Kopieren
              </button>
            </div>
          </li>
          <li>Hier auf „Verifizierung prüfen" klicken.</li>
        </ol>

        <div className="button-row">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submitConfirm}>
            {confirm.isPending ? 'Wird geprüft …' : 'Verifizierung prüfen'}
          </button>
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={abort}>
            Abbrechen
          </button>
        </div>
      </section>
    );
  }

  // Verifiziert, kein Versuch am Laufen -- Übersicht mit Option zum Neustart.
  if (user.gamePlayerVerifiedAt && !restarting) {
    return (
      <section className="card">
        <h2>Spielfigur</h2>
        <dl className="meta-grid">
          <div>
            <dt>Verifiziert als</dt>
            <dd>{user.gamePlayerName}</dd>
          </div>
          <div>
            <dt>Verifiziert am</dt>
            <dd>{formatDate(user.gamePlayerVerifiedAt)}</dd>
          </div>
        </dl>
        <p className="muted">
          Der Name lässt sich nur über eine erneute Verifizierung ändern — mit einem anderen
          Spielernamen als bisher.
        </p>
        <button type="button" className="btn" onClick={() => setRestarting(true)}>
          Neu verifizieren …
        </button>
      </section>
    );
  }

  // Schritt 1: Name angeben (auch beim Neustart einer bestehenden Verifizierung).
  return (
    <section className="card">
      <h2>Spielfigur verifizieren</h2>
      <p className="muted">
        Bevor du das Portal nutzen kannst, muss deine Spielfigur
        {user.world ? ` in „${user.world.name}"` : ''} verifiziert sein. Gib deinen Spielernamen
        genau so an, wie er im Spiel angezeigt wird — du bekommst danach einen Code, den du kurz in
        dein Spielerprofil einträgst.
      </p>

      {startError && <div className="flash flash-error">{startError.message}</div>}

      <form className="form" onSubmit={submitStart} noValidate>
        <label className="field">
          <span>Spielername</span>
          <input
            type="text"
            value={playerName}
            onChange={(event) => setPlayerName(event.target.value)}
            placeholder="Genau wie im Spiel angezeigt"
            maxLength={64}
            required
          />
        </label>

        <div className="button-row">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || playerName.trim().length === 0}
          >
            {start.isPending ? 'Wird vorbereitet …' : 'Code erzeugen'}
          </button>
          {restarting && (
            <button type="button" className="btn btn-ghost" onClick={() => setRestarting(false)}>
              Abbrechen
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
